import PosApi from "../api/PosApi";
import {
  CloverDeltaUpdate,
  CloverDeltaUpdateResponseStatusEnum,
  CloverRestaurantDto,
  DesignDto,
  ReplaceCloverItemDto,
  ReplaceSquareItemDto,
  ReplaceToastItemDto,
  SquareDeltaUpdate,
  SquareDeltaUpdateResponseStatusEnum,
  SquareRestaurantDto,
  ToastDeltaUpdate,
  ToastDeltaUpdateResponseStatusEnum,
  ToastRestaurantDto, TreezDeltaUpdate, TreezDeltaUpdateResponseStatusEnum, TreezRestaurantDto
} from "../generated/dashboard-client";
import {
  CloverUpdateByDesign,
  DeltaUpdate,
  Restaurants,
  SquareUpdateByDesign,
  ToastUpdateByDesign,
  TreezUpdateByDesign,
  UpdateByDesign
} from "../util/types";
import groupBy from "lodash/groupBy";
import { env } from "../env/env";
import { SetterOrUpdater } from "recoil";
import React from "react";
import { success } from "./MessageService";

export enum Pos {
  Toast, Square, Clover, Treez
}

export async function getAllToastDeltaUpdatesUnfiltered(toastRestaurants: ToastRestaurantDto[]) {
  const resp = await Promise.all(toastRestaurants.map(it => PosApi.getToastPendingChanges(it.restaurantGuid!!)));
  return resp.flatMap(it => it.data);
}

export async function getAllToastDeltaApprovedChangesUnfiltered(toastRestaurants: ToastRestaurantDto[]) {
  const resp = await Promise.all(toastRestaurants.map(it => PosApi.getToastApprovedChanges(it.restaurantGuid!!)));
  return resp.flatMap(it => it.data);
}

export async function getAllCloverDeltaUpdatesUnfiltered(cloverRestaurants: CloverRestaurantDto[]) {
  const resp = await Promise.all(cloverRestaurants.map(it => PosApi.getCloverPendingChanges(it.merchantId!!)));
  return resp.flatMap(it => it.data);
}

export async function getAllSquareDeltaUpdatesUnfiltered(squareRestaurants: SquareRestaurantDto[]) {
  const resp = await Promise.all(squareRestaurants.map(it => PosApi.getSquarePendingChanges(it.merchantId!!)));
  return resp.flatMap(it => it.data);
}

export async function getAllTreezDeltaUpdatesUnfiltered(treezRestaurants: TreezRestaurantDto[]) {
  const resp = await Promise.all(treezRestaurants.map(it => PosApi.getTreezPendingChanges(it.clientId!!)));
  return resp.flatMap(it => it.data);
}

export async function getToastDeltaUpdates(allUpdates: ToastDeltaUpdate[], toastRestaurants: ToastRestaurantDto[], designsUsingToast: DesignDto[]): Promise<ToastUpdateByDesign> {
  const allItems = designsUsingToast.flatMap(it => it.toastItems?.split(','));
  const allSections = designsUsingToast.flatMap(it => it.toastSections?.split(','));
  return mapToastDeltaUpdatesByDesign(allUpdates.filter(it => allItems.includes(it.itemId) || allSections.includes(it.sectionId)), designsUsingToast);
}

export async function getCloverDeltaUpdates(allUpdates: CloverDeltaUpdate[], cloverRestaurants: CloverRestaurantDto[], designsUsingClover: DesignDto[]) {
  const allItems = designsUsingClover.flatMap(it => it.cloverItems?.split(','));
  const allSections = designsUsingClover.flatMap(it => it.cloverSections?.split(','));
  return mapCloverDeltaUpdatesByDesign(allUpdates.filter(it => allItems.includes(it.itemId) || allSections.includes(it.sectionId)), designsUsingClover);
}

export async function getSquareDeltaUpdates(allUpdates: SquareDeltaUpdate[], squareRestaurants: SquareRestaurantDto[], designsUsingSquare: DesignDto[]) {
  const allItems = designsUsingSquare.flatMap(it => it.squareItems?.split(','));
  const allSections = designsUsingSquare.flatMap(it => it.squareSections?.split(','));
  return mapSquareDeltaUpdatesByDesign(allUpdates.filter(it => allItems.includes(it.itemId) || allSections.includes(it.sectionId)), designsUsingSquare);
}

export async function getTreezDeltaUpdates(allUpdates: TreezDeltaUpdate[], treezRestaurants: TreezRestaurantDto[], designsUsingTreez: DesignDto[]) {
  const allItems = designsUsingTreez.flatMap(it => it.treezItems?.split(','));
  const allSections = designsUsingTreez.flatMap(it => it.treezSections?.split(','));
  return mapTreezDeltaUpdatesByDesign(allUpdates.filter(it => allItems.includes(it.itemId) || allSections.includes(it.sectionId)), designsUsingTreez);
}

export async function mapToastDeltaUpdatesByDesign(changes: ToastDeltaUpdate[], designsUsingToast: DesignDto[]) {
  const deltaUpdates: ToastUpdateByDesign = {};
  changes.forEach(d => {
    designsUsingToast.forEach(it => {
      if (it.toastItems?.split(',').includes(d.itemId!!) ||
        it.toastSections?.split(',').includes(d.sectionId!!)) {
        const designId = it.id!!;
        if (!d.solvedForDesignIds?.includes(designId)) {
          if (!deltaUpdates[designId]) {
            deltaUpdates[designId] = {
              updates: [],
              solved: false
            }
          }
          deltaUpdates[designId].updates.push(d);
        }
      }
    });
  });

  return deltaUpdates;
}

export async function mapCloverDeltaUpdatesByDesign(changes: CloverDeltaUpdate[], designsUsingClover: DesignDto[]) {
  const deltaUpdates: CloverUpdateByDesign = {};
  changes.forEach(d => {
    designsUsingClover.forEach(it => {
      if (it.cloverItems?.split(',').includes(d.itemId!!) ||
        it.cloverSections?.split(',').includes(d.sectionId!!)) {
        const designId = it.id!!;
        if (!d.solvedForDesignIds?.includes(designId)) {
          if (!deltaUpdates[designId]) {
            deltaUpdates[designId] = {
              updates: [],
              solved: false
            }
          }
          deltaUpdates[designId].updates.push(d);
        }
      }
    });
  });
  return deltaUpdates;
}

export async function mapSquareDeltaUpdatesByDesign(changes: SquareDeltaUpdate[], designsUsingSquare: DesignDto[]) {
  const deltaUpdates: SquareUpdateByDesign = {};
  changes.forEach(d => {
    designsUsingSquare.forEach(it => {
      if (it.squareItems?.split(',').includes(d.itemId!!) ||
        it.squareSections?.split(',').includes(d.sectionId!!)) {
        const designId = it.id!!;
        if (!d.solvedForDesignIds?.includes(designId)) {
          if (!deltaUpdates[designId]) {
            deltaUpdates[designId] = {
              updates: [],
              solved: false
            }
          }
          deltaUpdates[designId].updates.push(d);
        }
      }
    });
  });
  return deltaUpdates;
}

export async function mapTreezDeltaUpdatesByDesign(changes: TreezDeltaUpdate[], designsUsingTreez: DesignDto[]) {
  const deltaUpdates: TreezUpdateByDesign = {};
  changes.forEach(d => {
    designsUsingTreez.forEach(it => {
      if (it.treezItems?.split(',').includes(d.itemId!!) ||
        it.treezSections?.split(',').includes(d.sectionId!!)) {
        const designId = it.id!!;
        if (!d.solvedForDesignIds?.includes(designId)) {
          if (!deltaUpdates[designId]) {
            deltaUpdates[designId] = {
              updates: [],
              solved: false
            }
          }
          deltaUpdates[designId].updates.push(d);
        }
      }
    });
  });
  return deltaUpdates;
}
export async function mapToastDeltaUpdatesToReplaceDtos(deltaUpdates: ToastUpdateByDesign) {
  const replaceDtos = [] as ReplaceToastItemDto[]

  Object.keys(deltaUpdates).forEach(designId => {
    replaceDtos.push({
      id: designId,
      items: deltaUpdates[designId].updates.map(it => {
        return {
          description: it.itemDescription,
          price: `${it.itemPrice}`,
          name: it.itemName,
          itemTags: it.itemTags || '',
          itemPriceMultipleJson: it.itemPriceMultipleJson || '',
          id: it.itemId,
          changeType: it.type,
          sectionId: it.sectionId,
          sectionExternalId: it.sectionExternalId
        }
      })
    })
  });

  return replaceDtos;
}

export async function mapCloverDeltaUpdatesToReplaceDtos(deltaUpdates: CloverUpdateByDesign) {
  const replaceDtos = [] as ReplaceCloverItemDto[]

  Object.keys(deltaUpdates).forEach(designId => {
    replaceDtos.push({
      id: designId,
      items: deltaUpdates[designId].updates.map(it => {
        return {
          description: it.itemDescription,
          price: `${it.itemPrice}`,
          name: it.itemName,
          id: it.itemId,
          changeType: it.type,
          sectionId: it.sectionId,
          sectionExternalId: it.sectionExternalId
        }
      })
    })
  });

  return replaceDtos;
}

export async function mapSquareDeltaUpdatesToReplaceDtos(deltaUpdates: SquareUpdateByDesign) {
  const replaceDtos = [] as ReplaceSquareItemDto[]

  Object.keys(deltaUpdates).forEach(designId => {
    replaceDtos.push({
      id: designId,
      items: deltaUpdates[designId].updates.map(it => {
        return {
          description: it.itemDescription,
          price: formatPrice(it.itemPrice),
          name: it.itemName,
          id: it.itemId,
          itemPriceMultipleJson: it.itemPriceMultipleJson || '',
          changeType: it.type,
          sectionId: it.sectionId,
          sectionExternalId: it.sectionExternalId
        }
      })
    })
  });

  return replaceDtos;
}

export async function mapTreezDeltaUpdatesToReplaceDtos(deltaUpdates: TreezUpdateByDesign) {
  const replaceDtos = [] as ReplaceSquareItemDto[]

  Object.keys(deltaUpdates).forEach(designId => {
    replaceDtos.push({
      id: designId,
      items: deltaUpdates[designId].updates.map(it => {
        return {
          description: it.itemDescription,
          price: formatPrice(it.itemPrice),
          name: it.itemName,
          id: it.itemId,
          itemPriceMultipleJson: it.itemPriceMultipleJson || '',
          changeType: it.type,
          sectionId: it.sectionId,
          sectionExternalId: it.sectionExternalId
        }
      })
    })
  });

  return replaceDtos;
}

export function countUnresolvedToastUpdates(toastDeltaUpdates: ToastUpdateByDesign) {
  let unresolved = 0;

  Object.keys(toastDeltaUpdates).forEach(key => {
    if (!toastDeltaUpdates[key].solved) {
      unresolved++;
    }
  });
  return unresolved;
}

export function hasUnresolvedToastUpdates(toastDeltaUpdates: ToastUpdateByDesign) {
  return countUnresolvedToastUpdates(toastDeltaUpdates) > 0;
}

export function hasUnresolvedCloverUpdates(cloverDeltaUpdates: CloverUpdateByDesign) {
  let hasChanges = false;

  Object.keys(cloverDeltaUpdates).forEach(key => {
    if (!cloverDeltaUpdates[key].solved) {
      hasChanges = true;
    }
  });
  return hasChanges;
}

export function hasUnresolvedSquareUpdates(squareDeltaUpdates: SquareUpdateByDesign) {
  let hasChanges = false;

  Object.keys(squareDeltaUpdates).forEach(key => {
    if (!squareDeltaUpdates[key].solved) {
      hasChanges = true;
    }
  });
  return hasChanges;
}

export function hasUnresolvedTreezUpdates(treezDeltaUpdates: TreezUpdateByDesign) {
  let hasChanges = false;

  Object.keys(treezDeltaUpdates).forEach(key => {
    if (!treezDeltaUpdates[key].solved) {
      hasChanges = true;
    }
  });
  return hasChanges;
}

export async function resolveAllToastPendingChanges(changes: ToastDeltaUpdate[]) {
  const updatesPerRestaurant = groupBy(changes, u => u.toastRestaurantId);

  for (const toastRestaurantId of Object.keys(updatesPerRestaurant)) {
    await PosApi.updateToastPendingChanges(toastRestaurantId, updatesPerRestaurant[toastRestaurantId].map(it => {
      return { changeId: it.id, status: ToastDeltaUpdateResponseStatusEnum.Approved }
    }));
  }
}

export async function resolveAllCloverPendingChanges(changes: CloverDeltaUpdate[]) {
  const updatesPerRestaurant = groupBy(changes, u => u.id);

  for (const restaurantId of Object.keys(updatesPerRestaurant)) {
    await PosApi.updateCloverPendingChanges(restaurantId, updatesPerRestaurant[restaurantId].map(it => {
      return { changeId: it.id, status: CloverDeltaUpdateResponseStatusEnum.Approved }
    }));
  }
}

export async function resolveAllSquarePendingChanges(changes: SquareDeltaUpdate[]) {
  const updatesPerRestaurant = groupBy(changes, u => u.id);

  for (const restaurantId of Object.keys(updatesPerRestaurant)) {
    await PosApi.updateSquarePendingChanges(restaurantId, updatesPerRestaurant[restaurantId].map(it => {
      return { changeId: it.id, status: SquareDeltaUpdateResponseStatusEnum.Approved }
    }));
  }
}
export async function resolveAllTreezPendingChanges(changes: TreezDeltaUpdate[]) {
  const updatesPerRestaurant = groupBy(changes, u => u.id);

  for (const restaurantId of Object.keys(updatesPerRestaurant)) {
    await PosApi.updateTreezPendingChanges(restaurantId, updatesPerRestaurant[restaurantId].map(it => {
      return { changeId: it.id, status: TreezDeltaUpdateResponseStatusEnum.Approved }
    }));
  }
}

export function goToDesign(designId: string) {
  window.location.href = getDesignTimberLink(designId);
}

export function getDesignTimberLink(designId: string) {
  return `${env.timberContextPath}/design?id=${designId}`;
}

export function getDesignName(designs: DesignDto[], designId: string) {
  return designs.find(it => it.id === designId)?.name || '';
}

export function getDesign(designs: DesignDto[], designId: string) {
  return designs.find(it => it.id === designId) || null;
}

export function getUpdateItemCount(updateByDesign: UpdateByDesign, designId: string) {
  return updateByDesign[designId]?.updates.flatMap(it => it.itemName).length;
}

export function removeMenu(designId: string, setDeltaUpdates: SetterOrUpdater<UpdateByDesign>) {
  setDeltaUpdates(old => {
    const copy = { ...old };
    copy[designId] = {
      updates: copy[designId].updates,
      solved: true
    }
    return copy;
  });
}

export async function updateTimberItems(designId: string, designsUsingPOS: DesignDto[], changes: DeltaUpdate[], pos: Pos) {
  if (pos === Pos.Toast) {
    const deltaUpdates = await mapToastDeltaUpdatesByDesign(changes, designsUsingPOS);
    const replaceDtos = await mapToastDeltaUpdatesToReplaceDtos(deltaUpdates);
    const replaceDtosForDesign = replaceDtos.filter(it => it.id === designId);
    await PosApi.replaceToastItems(replaceDtosForDesign);
  }

  if (pos === Pos.Clover) {
    const deltaUpdates = await mapCloverDeltaUpdatesByDesign(changes, designsUsingPOS);
    const replaceDtos = await mapCloverDeltaUpdatesToReplaceDtos(deltaUpdates);
    const replaceDtosForDesign = replaceDtos.filter(it => it.id === designId);
    await PosApi.replaceCloverItems(replaceDtosForDesign);
  }

  if (pos === Pos.Square) {
    const deltaUpdates = await mapSquareDeltaUpdatesByDesign(changes, designsUsingPOS);
    const replaceDtos = await mapSquareDeltaUpdatesToReplaceDtos(deltaUpdates);
    const replaceDtosForDesign = replaceDtos.filter(it => it.id === designId);
    await PosApi.replaceSquareItems(replaceDtosForDesign);
  }

  if (pos === Pos.Treez) {
    const deltaUpdates = await mapTreezDeltaUpdatesByDesign(changes, designsUsingPOS);
    const replaceDtos = await mapTreezDeltaUpdatesToReplaceDtos(deltaUpdates);
    const replaceDtosForDesign = replaceDtos.filter(it => it.id === designId);
    await PosApi.replaceTreezItems(replaceDtosForDesign);
  }
}

export async function updatePendingChanges(designId: string, changes: DeltaUpdate[], restaurants: Restaurants, pos: Pos) {
  if (!restaurants.length) return;

  for (const change of changes) {
    if (pos === Pos.Toast) {
      await PosApi.addToastChangeSolvedInDesignId((change as ToastDeltaUpdate).toastRestaurantId!!, change.id!!, { designId });
    }
    if (pos === Pos.Clover) {
      await PosApi.addCloverChangeSolvedInDesignId(change.id!!, change.id!!, { designId });
    }
    if (pos === Pos.Square) {
      await PosApi.addSquareChangeSolvedInDesignId(change.id!!, change.id!!, { designId });
    }
    if (pos === Pos.Treez) {
      await PosApi.addTreezChangeSolvedInDesignId(change.id!!, change.id!!, { designId });
    }
  }
}

export async function updateMenuItems(
  designId: string,
  restaurants: Restaurants,
  pos: Pos,
  deltaUpdates: UpdateByDesign,
  setDeltaUpdates: SetterOrUpdater<UpdateByDesign>,
  setOptionsModalDesignId: React.Dispatch<React.SetStateAction<string>>) {

  if (!restaurants.length) return;

  await updatePendingChanges(designId, deltaUpdates[designId].updates, restaurants, pos);
  removeMenu(designId, setDeltaUpdates);
  setOptionsModalDesignId('');
}

export async function onReviewApprove(
  ids: string[],
  setReviewModal: React.Dispatch<React.SetStateAction<boolean>>,
  setLoadingDesignIds: React.Dispatch<React.SetStateAction<string[]>>,
  reviewDesignId: string,
  restaurants: Restaurants,
  deltaUpdates: UpdateByDesign,
  designs: DesignDto[],
  setDesigns: React.Dispatch<React.SetStateAction<DesignDto[]>>,
  pos: Pos,
  setDeltaUpdates: SetterOrUpdater<UpdateByDesign>,
  setOptionsModalDesignId: React.Dispatch<React.SetStateAction<string>>) {

  setReviewModal(false);

  setLoadingDesignIds([reviewDesignId]);
  const approvedChanges = deltaUpdates[reviewDesignId].updates.filter(it => ids.includes(it.id!!));
  const dismissedChanges = deltaUpdates[reviewDesignId].updates.filter(it => !ids.includes(it.id!!));

  await updateTimberItems(reviewDesignId, designs, approvedChanges, pos);
  await updatePendingChanges(reviewDesignId, approvedChanges, restaurants, pos);
  await updatePendingChanges(reviewDesignId, dismissedChanges, restaurants, pos);
  removeMenu(reviewDesignId, setDeltaUpdates);
  setOptionsModalDesignId('');

  markDesignPosLastUpdate(designs, setDesigns, reviewDesignId);
  setLoadingDesignIds([]);
  let successMsg = '';
  if (approvedChanges.length) {
    successMsg = `Approved ${approvedChanges.length}`;
    if (dismissedChanges.length) {
      successMsg += ` and dismissed ${dismissedChanges.length}`;
    }
  } else {
    successMsg = `Dismissed ${approvedChanges.length}`;
  }

  successMsg += ` changes from ${getDesignName(designs, reviewDesignId)}`;

  success(successMsg);
}

export async function approveAll(
  e: React.MouseEvent<HTMLElement>,
  designId: string,
  setLoadingDesignIds: React.Dispatch<React.SetStateAction<string[]>>,
  setOptionsModalDesignId: React.Dispatch<React.SetStateAction<string>>,
  designs: DesignDto[],
  setDesigns: React.Dispatch<React.SetStateAction<DesignDto[]>>,
  pos: Pos,
  restaurants: Restaurants,
  deltaUpdates: UpdateByDesign,
  setDeltaUpdates: SetterOrUpdater<UpdateByDesign>
) {
  setLoadingDesignIds([designId]);
  await updateTimberItems(designId, designs, deltaUpdates[designId].updates, pos);
  await updateMenuItems(designId, restaurants, pos, deltaUpdates, setDeltaUpdates, setOptionsModalDesignId);
  markDesignPosLastUpdate(designs, setDesigns, designId);
  success(`Item changes from ${getDesignName(designs, designId)} were approved.`);
  setLoadingDesignIds([]);
}

export async function approveAllForDesignIds(
  e: React.MouseEvent<HTMLElement>,
  designIds: string[],
  setLoadingDesignIds: React.Dispatch<React.SetStateAction<string[]>>,
  setOptionsModalDesignId: React.Dispatch<React.SetStateAction<string>>,
  designs: DesignDto[],
  setDesigns: React.Dispatch<React.SetStateAction<DesignDto[]>>,
  pos: Pos,
  restaurants: Restaurants,
  deltaUpdates: UpdateByDesign,
  setDeltaUpdates: SetterOrUpdater<UpdateByDesign>
) {
  setLoadingDesignIds(designIds);
  for (const designId of designIds) {
    await updateTimberItems(designId, designs, deltaUpdates[designId].updates, pos);
    await updateMenuItems(designId, restaurants, pos, deltaUpdates, setDeltaUpdates, setOptionsModalDesignId);
    markDesignPosLastUpdate(designs, setDesigns, designId);
    success(`Item changes from ${getDesignName(designs, designId)} were approved.`);
  }
  setLoadingDesignIds([]);
}

function markDesignPosLastUpdate(designs: DesignDto[], setDesigns: React.Dispatch<React.SetStateAction<DesignDto[]>>, designId: string) {
  setDesigns(designs.map(it => {
    if (it.id === designId) {
      return { ...it, posUpdatedDate: new Date().toISOString() };
    }
    return it;
  }));
}

export async function ignore(
  e: React.MouseEvent<HTMLElement>,
  designId: string,
  setLoadingDesignIds: React.Dispatch<React.SetStateAction<string[]>>,
  restaurants: Restaurants,
  pos: Pos,
  deltaUpdates: UpdateByDesign,
  setDeltaUpdates: SetterOrUpdater<UpdateByDesign>,
  setOptionsModalDesignId: React.Dispatch<React.SetStateAction<string>>,
  designs: DesignDto[],
) {

  setLoadingDesignIds([designId]);
  await updateMenuItems(designId, restaurants, pos, deltaUpdates, setDeltaUpdates, setOptionsModalDesignId);
  success(`Item changes from ${getDesignName(designs, designId)} were dismissed.`);
  setLoadingDesignIds([]);
}

export function review(
  e: React.MouseEvent<HTMLElement>,
  designId: string,
  setReviewModal: React.Dispatch<React.SetStateAction<boolean>>,
  setReviewDesignId: React.Dispatch<React.SetStateAction<string>>,
  deltaUpdates: UpdateByDesign,
  setToastReviewUpdates: React.Dispatch<React.SetStateAction<ToastDeltaUpdate[]>>
) {
  setToastReviewUpdates(deltaUpdates[designId].updates);
  setReviewDesignId(designId);
  setReviewModal(true);
}

export function getRendererUrl(designId: string) {
  return `${env.rendererPath}/design/thumbnail/${designId}?width=110`;
}

function formatPrice(price: number | undefined) {
  if (!price) return '';
  const priceNr = parseFloat(`${price}`);
  const formatted = `${priceNr}`;
  if (formatted.indexOf('.') > -1) {
    return priceNr.toFixed(2);
  } else {
    return formatted;
  }
}
